import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { listarProjetos } from '@/features/projetos/projetos.api';
import {
  listarSprints,
  criarSprint,
  atualizarSprint,
  excluirSprint,
  type Sprint,
} from '@/features/projetos/sprints.api';
import { cn } from '@/lib/cn';

const STATUS_LABEL: Record<string, string> = {
  PLANNING: 'Planejamento',
  ACTIVE: 'Ativo',
  COMPLETED: 'Concluído',
};

const STATUS_CLS: Record<string, string> = {
  PLANNING: 'text-muted-foreground bg-muted/50',
  ACTIVE: 'text-primary bg-primary/10',
  COMPLETED: 'text-green-600 bg-green-50 dark:bg-green-900/20',
};

function fmtEpoch(ep: number) {
  return new Date(ep * 60 * 1000).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function epochFromDate(s: string) {
  return Math.floor(new Date(s).getTime() / 60_000);
}

const FORM_VAZIO = { name: '', goal: '', startAt: '', endAt: '' };

export function SprintsPage() {
  const qc = useQueryClient();
  const [projetoId, setProjetoId] = useState('');
  const [criando, setCriando] = useState(false);
  const [form, setForm] = useState(FORM_VAZIO);

  const projetos = useQuery({
    queryKey: ['projetos', 'lista', 'ACTIVE'],
    queryFn: () => listarProjetos({ status: 'ACTIVE' }),
  });

  const sprints = useQuery({
    queryKey: ['projetos', 'sprints', projetoId],
    queryFn: () => listarSprints(projetoId),
    enabled: !!projetoId,
  });

  const criarMut = useMutation({
    mutationFn: () =>
      criarSprint(projetoId, {
        name: form.name,
        goal: form.goal || undefined,
        startAt: epochFromDate(form.startAt),
        endAt: epochFromDate(form.endAt),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['projetos', 'sprints', projetoId] });
      setCriando(false);
      setForm(FORM_VAZIO);
    },
  });

  const statusMut = useMutation({
    mutationFn: ({ sprintId, status }: { sprintId: string; status: string }) =>
      atualizarSprint(projetoId, sprintId, { status }),
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: ['projetos', 'sprints', projetoId] }),
  });

  const excluirMut = useMutation({
    mutationFn: (sprintId: string) => excluirSprint(projetoId, sprintId),
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: ['projetos', 'sprints', projetoId] }),
  });

  const lista: Sprint[] = sprints.data ?? [];

  return (
    <div className="mx-auto max-w-[1000px] space-y-4">
      <header className="flex items-center justify-between gap-4 border-b border-border pb-3">
        <h1 className="text-base font-semibold">Sprints</h1>
        <div className="flex items-center gap-2">
          <select
            value={projetoId}
            onChange={(e) => setProjetoId(e.target.value)}
            className="h-8 rounded-lg border border-input/80 bg-muted/40 px-2 text-sm focus-visible:outline-none"
          >
            <option value="">Selecione um projeto…</option>
            {projetos.data?.items.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
          {projetoId && (
            <button
              onClick={() => setCriando(true)}
              className="flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90"
            >
              <Plus className="h-3.5 w-3.5" /> Nova sprint
            </button>
          )}
        </div>
      </header>

      {!projetoId ? (
        <div className="py-8 text-center text-sm text-muted-foreground">
          Selecione um projeto para ver suas sprints.
        </div>
      ) : sprints.isPending ? (
        <div className="py-8 text-center text-sm text-muted-foreground">Carregando…</div>
      ) : lista.length === 0 ? (
        <div className="py-8 text-center text-sm text-muted-foreground">
          Nenhuma sprint criada.
        </div>
      ) : (
        <div className="space-y-3">
          {lista.map((s) => {
            const total = s.tasks.length;
            const done = s.tasks.filter((t) => t.progress === 100).length;
            const pct = total > 0 ? Math.round((done / total) * 100) : 0;
            return (
              <div
                key={s.id}
                className="rounded-xl border border-border bg-background p-4 space-y-3"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm">{s.name}</span>
                      <span
                        className={cn(
                          'rounded-full px-2 py-0.5 text-[10px] font-medium',
                          STATUS_CLS[s.status],
                        )}
                      >
                        {STATUS_LABEL[s.status]}
                      </span>
                    </div>
                    {s.goal && (
                      <p className="text-xs text-muted-foreground mt-0.5">{s.goal}</p>
                    )}
                    <p className="text-[10px] text-muted-foreground mt-1">
                      {fmtEpoch(s.startAt)} → {fmtEpoch(s.endAt)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {s.status === 'PLANNING' && (
                      <button
                        onClick={() => statusMut.mutate({ sprintId: s.id, status: 'ACTIVE' })}
                        className="rounded-lg bg-primary/10 px-3 py-1 text-xs font-medium text-primary hover:bg-primary/20"
                      >
                        Iniciar
                      </button>
                    )}
                    {s.status === 'ACTIVE' && (
                      <button
                        onClick={() =>
                          statusMut.mutate({ sprintId: s.id, status: 'COMPLETED' })
                        }
                        className="rounded-lg bg-green-100 dark:bg-green-900/20 px-3 py-1 text-xs font-medium text-green-600 hover:bg-green-200 dark:hover:bg-green-900/40"
                      >
                        Concluir
                      </button>
                    )}
                    <button
                      onClick={() => {
                        if (window.confirm(`Excluir "${s.name}"?`)) excluirMut.mutate(s.id);
                      }}
                      className="text-[10px] text-muted-foreground hover:text-destructive"
                    >
                      Excluir
                    </button>
                  </div>
                </div>
                <div className="space-y-1">
                  <div className="flex justify-between text-[10px] text-muted-foreground">
                    <span>
                      {done}/{total} tarefas
                    </span>
                    <span>{pct}%</span>
                  </div>
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                    <div
                      className={cn(
                        'h-full rounded-full',
                        pct === 100 ? 'bg-green-500' : 'bg-primary',
                      )}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal: nova sprint */}
      {criando && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-sm rounded-xl border border-border bg-background p-6 space-y-4 shadow-2xl">
            <h2 className="text-base font-semibold">Nova sprint</h2>
            <div className="space-y-3">
              {(
                [
                  { label: 'Nome', key: 'name', type: 'text', placeholder: 'Sprint 1' },
                  { label: 'Objetivo (opcional)', key: 'goal', type: 'text', placeholder: '' },
                  { label: 'Início', key: 'startAt', type: 'date', placeholder: '' },
                  { label: 'Fim', key: 'endAt', type: 'date', placeholder: '' },
                ] as const
              ).map(({ label, key, type, placeholder }) => (
                <div key={key} className="space-y-1">
                  <label className="text-xs text-muted-foreground">{label}</label>
                  <input
                    type={type}
                    value={form[key]}
                    placeholder={placeholder}
                    onChange={(e) => setForm((p) => ({ ...p, [key]: e.target.value }))}
                    className="h-9 w-full rounded-lg border border-input/80 bg-muted/40 px-3 text-sm focus-visible:outline-none focus-visible:border-ring/40"
                  />
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setCriando(false)}
                className="rounded-lg px-4 py-2 text-sm text-muted-foreground hover:bg-muted"
              >
                Cancelar
              </button>
              <button
                onClick={() => criarMut.mutate()}
                disabled={!form.name || !form.startAt || !form.endAt || criarMut.isPending}
                className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-40"
              >
                {criarMut.isPending ? 'Criando…' : 'Criar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
